import { retrieveAuthTokens } from '../../../utils/next/auth.utils';

// The backend artifact manifest emits a bare '/api/v1/artifacts/{id}/presign'
// path. That segment is NOT the chat-agent service prefix the console proxies
// (`/api/chat-agent/v1/...`), so a relative fetch resolves against the SPA
// origin and returns the index.html shell instead of the presign JSON. Rewrite
// the bare '/api/v1/' prefix onto the chat-agent base (idempotent for already
// -correct or absolute URLs) so the request reaches the presign route.
export function resolveArtifactDownloadUrl(downloadUrl: string): string {
  if (/^https?:\/\//i.test(downloadUrl)) return downloadUrl;
  if (downloadUrl.startsWith('/api/chat-agent/')) return downloadUrl;
  if (downloadUrl.startsWith('/api/v1/')) {
    return downloadUrl.replace('/api/v1/', '/api/chat-agent/v1/');
  }
  return downloadUrl;
}

// Same-origin check: the session bearer token may only ride on requests back
// to our own origin. `resolveArtifactDownloadUrl` passes absolute URLs through
// untouched, so a presigned S3 link (or any agent-emitted absolute URL) stays
// cross-origin — attaching the JWT there would leak it and can also break the
// presigned signature (the extra header isn't part of it).
function isSameOrigin(url: string): boolean {
  if (globalThis.location === undefined) return false;
  try {
    return (
      new URL(url, globalThis.location.origin).origin ===
      globalThis.location.origin
    );
  } catch {
    return false;
  }
}

// Hand the browser a URL to download without navigating the SPA away: a
// transient hidden anchor click. Nothing to revoke — unlike a blob objectURL,
// the browser streams the (presigned, cross-origin) URL directly. The
// `download` attribute is only a same-origin hint (browsers ignore it
// cross-origin); the URL's own Content-Disposition drives the saved name.
function triggerBrowserNavigate(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Batch downloads can't reuse the anchor above. Because these URLs are
// cross-origin the `download` attribute is ignored, so each click is a
// *navigation* that happens to save a file (via Content-Disposition) — and
// successive navigations in the same frame cancel one another, so only the
// first file ever arrived. A per-file hidden iframe gives each download its own
// frame to navigate, which is the dependency-free way to run several at once.
//
// The iframe is left attached: removing it can abort a download that hasn't
// finished streaming. They're empty and inert, so a handful per batch is
// cheaper than the alternative of losing files.
function triggerFrameDownload(url: string): void {
  const frame = document.createElement('iframe');
  frame.style.display = 'none';
  frame.src = url;
  document.body.appendChild(frame);
}

// Presigned-URL download: an allowed native-fetch exception (see CLAUDE.md API
// rules — signed-URL downloads that don't fit the axios JSON flow). The proxied
// same-origin presign endpoint returns a tiny JSON body `{ url, filename }`
// where `url` is a short-lived (1hr) presigned S3 link. We fetch only that JSON
// — the session bearer rides the same-origin presign call and never leaves our
// origin — then hand the browser the S3 URL so it streams the file directly
// with native progress and no client-side buffering. The presigned URL carries
// `Content-Disposition: attachment`, so it saves with the right name. This
// host-specific auth stays in the console app rather than the shared
// `@cowbell/ui` surface; the surface reaches it via the `DownloadArtifact`
// action handled in onA2UIAction.
/** Fetch an artifact's raw bytes, for callers that need the content rather
 *  than a save prompt — zipping a batch, for one.
 *
 *  Point this at the `/download` route (not `/presign`, which returns JSON):
 *  it 302s to the presigned S3 URL, the same shape the KB view endpoint uses,
 *  so the bearer rides the same-origin call and the browser drops it when the
 *  redirect crosses to S3. */
export async function fetchArtifactBytes(url: string): Promise<Uint8Array> {
  const resolvedUrl = resolveArtifactDownloadUrl(url);
  const headers: Record<string, string> = {};
  if (isSameOrigin(resolvedUrl)) {
    const { accessToken } = retrieveAuthTokens();
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  }
  const response = await fetch(resolvedUrl, { headers });
  if (!response.ok) {
    throw new Error(`Artifact fetch failed: ${response.status}`);
  }
  return new Uint8Array(await response.arrayBuffer());
}

export async function downloadArtifact(
  downloadUrl: string,
  filename: string,
  /** Set for batch downloads, which need their own frame per file — see
   *  `triggerFrameDownload`. A single download keeps the anchor path. */
  options: { readonly batched?: boolean } = {}
): Promise<void> {
  const resolvedUrl = resolveArtifactDownloadUrl(downloadUrl);
  const headers: Record<string, string> = {};
  if (isSameOrigin(resolvedUrl)) {
    const { accessToken } = retrieveAuthTokens();
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(resolvedUrl, { headers });
  if (!response.ok) {
    throw new Error(`Artifact download failed: ${response.status}`);
  }

  const body = (await response.json()) as { url?: string; filename?: string };
  if (!body.url) {
    throw new Error('Artifact download failed: presign response missing url');
  }

  if (options.batched) {
    triggerFrameDownload(body.url);
    return;
  }
  triggerBrowserNavigate(body.url, body.filename ?? filename);
}
